/**
 * RS Bezel PDP — gallery, style picker, bundles, extras, ATC.
 * Data from #rs-pdp-data (Liquid-generated JSON).
 */
(function () {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  const dataEl = document.getElementById("rs-pdp-data");
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error("RS Bezel PDP: invalid product data", e);
    return;
  }

  if (data.pdpMode !== "bezel") return;

  const root = document.querySelector(".rs-pdp");
  if (!root) return;

  const moneyOptions = () => ({
    currencyCodeEnabled: data.currencyCodeEnabled,
    currencyCode: data.currencyCode,
    locale: data.locale,
  });

  const formatMoney = (cents) =>
    window.RsMoney?.format(cents, moneyOptions()) ?? String(cents);

  const findBezel = (handle) =>
    data.bezels.find((b) => b.handle === handle) || data.current;

  const resolveInitialBundleQty = () => {
    const availableQtys = (data.bundles || []).map((bundle) => bundle.qty);
    if (!availableQtys.length) return 0;

    // 0 = none selected (extras grid stays hidden). Do not fall back to max/popular.
    let qty = Number(data.defaultBundleQty);
    if (!Number.isFinite(qty) || qty < 0) qty = 0;
    if (qty === 0) return 0;
    if (!availableQtys.includes(qty)) return 0;
    return qty;
  };

  const state = {
    currentHandle: data.current.handle,
    previewBezelHandle: null,
    dialHandle: data.dials[0]?.handle || null,
    previewDialHandle: null,
    activeGalleryId: "hero",
    bundleQty: resolveInitialBundleQty(),
    extraQty: Object.fromEntries(data.bezels.map((b) => [b.handle, 0])),
  };

  const currentBezel = () => findBezel(state.currentHandle);
  const activeBezel = () => findBezel(state.previewBezelHandle ?? state.currentHandle);
  const activeDial = () =>
    data.dials.find((d) => d.handle === (state.previewDialHandle ?? state.dialHandle)) ||
    data.dials[0];

  const Gallery = window.RsPdpGallery || {};
  const PLAY_ICON = Gallery.PLAY_ICON || "";
  const isVideo = Gallery.isVideo || ((item) => item?.mediaType === "video" || item?.mediaType === "external_video");
  const buildVideoMedia = Gallery.buildVideoMedia;
  const renderHeroMediaShared = Gallery.renderHeroMedia;
  const mapProductImages = Gallery.mapProductImages;

  const buildGalleryImages = () => {
    const bezel = activeBezel();
    const images = mapProductImages
      ? mapProductImages(bezel)
      : [];

    // Fallback when product media JSON is empty
    if (!images.length && (bezel.featuredImage || bezel.thumb)) {
      images.push({
        id: "hero",
        src: bezel.featuredImage || bezel.thumb,
        alt: `${bezel.label} interchangeable bezel`,
        mediaType: "image",
        sources: [],
        embedUrl: "",
      });
    } else {
      images.forEach((img, i) => {
        if (i === 0) {
          img.alt = img.alt || `${bezel.label} interchangeable bezel`;
        }
      });
    }

    data.dials.forEach((dial) => {
      if (!dial.previewImage) return;
      images.push({
        id: dial.galleryId || `watch-${dial.handle}`,
        src: dial.previewImage,
        alt: `Pivot ${dial.label.replace(" sunray", "")} with ${bezel.label} bezel`,
        mediaType: "image",
        sources: [],
        embedUrl: "",
      });
    });

    return images.filter((img) => img.src);
  };

  const renderHeroMedia = (item, animate = true, forcePlay = false) => {
    const fig = $("#gallery-hero", root);
    if (renderHeroMediaShared) {
      renderHeroMediaShared(fig, item, {
        autoplay: data.videoAutoplay,
        animate,
        forcePlay,
      });
      return;
    }
    if (!fig || !item?.src) return;
    let img = $("#hero-image", fig);
    if (!img) {
      fig.innerHTML =
        '<img id="hero-image" width="1200" height="1200" decoding="async" alt="" />';
      img = $("#hero-image", fig);
    }
    if (animate) img.classList.add("is-swapping");
    img.src = item.src;
    img.alt = item.alt || "";
    if (animate) requestAnimationFrame(() => img.classList.remove("is-swapping"));
  };

  const activeGalleryImage = (images) => {
    if (!images.some((i) => i.id === state.activeGalleryId)) {
      state.activeGalleryId = "hero";
    }
    return images.find((i) => i.id === state.activeGalleryId) ?? images[0];
  };

  const renderGallery = () => {
    const images = buildGalleryImages();
    if (!images.length) return;

    const active = activeGalleryImage(images);
    const animate = !state.previewBezelHandle && !state.previewDialHandle;
    renderHeroMedia(active, animate, false);

    const grid = $("#gallery-grid", root);
    if (grid) {
      grid.innerHTML = "";
      images
        .filter((i) => i.id !== "hero")
        .forEach((item) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = `gallery__thumb${state.activeGalleryId === item.id ? " is-active" : ""}${isVideo(item) ? " gallery__thumb--video" : ""}`;
          btn.setAttribute("aria-label", `View ${item.alt}`);
          const thumbSrc = (item.thumb || item.src || "").replace("width=1200", "width=400");
          btn.innerHTML = `<img src="${thumbSrc}" alt="" loading="lazy" decoding="async" width="400" height="400" />${isVideo(item) ? `<span class="gallery__thumb-play">${PLAY_ICON}</span>` : ""}`;
          btn.addEventListener("click", () => {
            state.activeGalleryId = item.id;
            renderHeroMedia(item, true, isVideo(item));
            $$(".gallery__thumb", grid).forEach((el) => el.classList.remove("is-active"));
            btn.classList.add("is-active");
            $("#gallery-hero", root)?.scrollIntoView({ behavior: "smooth", block: "start" });

            const slideIndex = images.findIndex((img) => img.id === item.id);
            if (slideIndex >= 0) scrollToSlide(slideIndex);
          });
          grid.appendChild(btn);
        });
    }

    renderMobileSlider(images);
  };

  const getCurrentSlide = () => {
    const track = $("#slider-track", root);
    if (!track?.firstElementChild) return 0;
    return Math.round(track.scrollLeft / track.firstElementChild.offsetWidth);
  };

  const scrollToSlide = (index, smooth = true) => {
    const track = $("#slider-track", root);
    if (!track) return;
    const slides = $$(".gallery-slider__slide", track);
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    track.scrollTo({
      left: slides[clamped]?.offsetLeft || clamped * slides[0].offsetWidth,
      behavior: smooth ? "smooth" : "instant",
    });
    syncSliderDots(clamped);
  };

  const syncSliderDots = (index) => {
    $$(".gallery-slider__dot", root).forEach((dot, i) =>
      dot.classList.toggle("is-active", i === index)
    );
  };

  const onSliderScroll = () => {
    const images = buildGalleryImages();
    const current = getCurrentSlide();
    syncSliderDots(current);
    if (images[current]) state.activeGalleryId = images[current].id;
  };

  let sliderNavBound = false;

  const bindSliderNav = () => {
    if (sliderNavBound) return;
    sliderNavBound = true;
    $("#slider-prev", root)?.addEventListener("click", () => scrollToSlide(getCurrentSlide() - 1));
    $("#slider-next", root)?.addEventListener("click", () => scrollToSlide(getCurrentSlide() + 1));
    $("#slider-track", root)?.addEventListener("scroll", onSliderScroll, { passive: true });
  };

  const renderMobileSlider = (images) => {
    const track = $("#slider-track", root);
    const dotsContainer = $("#slider-dots", root);
    if (!track || !dotsContainer) return;

    track.innerHTML = "";
    dotsContainer.innerHTML = "";

    const activeIndex = Math.max(0, images.findIndex((i) => i.id === state.activeGalleryId));

    images.forEach((item, i) => {
      const slide = document.createElement("div");
      slide.className = "gallery-slider__slide";
      if (isVideo(item) && buildVideoMedia) {
        slide.appendChild(buildVideoMedia(item, data.videoAutoplay));
      } else {
        slide.innerHTML = `<img src="${item.src}" alt="${item.alt}" loading="${i === 0 ? "eager" : "lazy"}" decoding="async" width="800" height="800" />`;
      }
      track.appendChild(slide);

      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = `gallery-slider__dot${i === activeIndex ? " is-active" : ""}`;
      dot.setAttribute("role", "tab");
      dot.setAttribute("aria-label", `Slide ${i + 1}`);
      dot.addEventListener("click", () => {
        state.activeGalleryId = item.id;
        scrollToSlide(i);
        // Keep desktop hero in sync when dots are used at larger breakpoints
        renderHeroMedia(item, true, isVideo(item));
      });
      dotsContainer.appendChild(dot);
    });

    bindSliderNav();
    requestAnimationFrame(() => scrollToSlide(activeIndex, false));
  };

  const renderBezelSwatches = () => {
    const grid = $("#bezel-group .bezel-picker-grid", root);
    if (!grid) return;

    grid.innerHTML = "";
    data.bezels.forEach((bezel) => {
      const cell = document.createElement("div");
      cell.className = "bezel-picker";

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "swatch swatch--bezel";
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", String(state.currentHandle === bezel.handle));
      btn.setAttribute("aria-label", bezel.label);
      if (bezel.thumb) btn.style.setProperty("--swatch-image", `url("${bezel.thumb}")`);
      btn.title = bezel.label;

      btn.addEventListener("mouseenter", () => {
        state.previewBezelHandle = bezel.handle;
        state.activeGalleryId = "hero";
        renderGallery();
        const label = $("#bezel-label", root);
        if (label) label.textContent = bezel.label;
      });

      btn.addEventListener("mouseleave", () => {
        state.previewBezelHandle = null;
        state.activeGalleryId = "hero";
        renderGallery();
        const label = $("#bezel-label", root);
        if (label) label.textContent = currentBezel().label;
      });

      btn.addEventListener("click", () => {
        if (state.currentHandle === bezel.handle) return;
        window.location.href = bezel.url;
      });

      cell.appendChild(btn);
      grid.appendChild(cell);
    });
  };

  const renderDialSwatches = () => {
    const row = $("#dial-group .swatch-row", root);
    if (!row || !data.dials.length) return;

    row.innerHTML = "";
    data.dials.forEach((dial) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "swatch";
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", String(state.dialHandle === dial.handle));
      btn.setAttribute("aria-label", dial.label);
      btn.style.setProperty("--swatch-color", dial.color);
      btn.title = dial.label;

      btn.addEventListener("mouseenter", () => {
        state.previewDialHandle = dial.handle;
        state.activeGalleryId = dial.galleryId || `watch-${dial.handle}`;
        renderGallery();
        const label = $("#dial-label", root);
        if (label) label.textContent = dial.label;
      });

      btn.addEventListener("mouseleave", () => {
        state.previewDialHandle = null;
        state.activeGalleryId = "hero";
        renderGallery();
        const label = $("#dial-label", root);
        if (label) label.textContent = activeDial()?.label || "";
      });

      btn.addEventListener("click", () => {
        state.dialHandle = dial.handle;
        state.previewDialHandle = null;
        state.activeGalleryId = dial.galleryId || `watch-${dial.handle}`;
        row.querySelectorAll(".swatch").forEach((el) => el.setAttribute("aria-checked", "false"));
        btn.setAttribute("aria-checked", "true");
        updateProductCopy();
        renderGallery();
      });

      row.appendChild(btn);
    });
  };

  const totalExtraQty = () =>
    Object.entries(state.extraQty).reduce((sum, [handle, qty]) => {
      if (handle === state.currentHandle) return sum;
      return sum + qty;
    }, 0);

  /** Bundle qty = extra bezels on top of the style on this page (1 → 1 extra, 2 → 2, …). */
  const extraSlots = () => Math.max(0, state.bundleQty || 0);

  const availableExtraBezels = () =>
    (data.bezels || []).filter(
      (bezel) => bezel.handle !== state.currentHandle && (bezel.variantId || bezel.bezelProductVariantId)
    );

  const clearExtras = () => {
    (data.bezels || []).forEach((bezel) => {
      state.extraQty[bezel.handle] = 0;
    });
  };

  const preselectExtras = (count) => {
    clearExtras();
    availableExtraBezels().slice(0, Math.max(0, count)).forEach((bezel) => {
      state.extraQty[bezel.handle] = 1;
    });
  };

  const activeBundle = () =>
    data.bundles?.find((b) => b.qty === state.bundleQty) ?? null;

  const bundlePricing = (bundle) => ({
    price: bundle.price,
    compareAt: bundle.compareAt,
  });

  const renderBundles = () => {
    const container = $("#bezel-bundles", root);
    if (!container || !data.bundles?.length) return;

    container.innerHTML = "";
    data.bundles.forEach((bundle) => {
      const selected = state.bundleQty === bundle.qty;
      const { price, compareAt } = bundlePricing(bundle);
      const savings = compareAt - price;
      const percent =
        compareAt > price
          ? Math.round((1 - price / compareAt) * 100)
          : 0;
      const label = bundle.label || (bundle.qty === 1 ? "1 bezel" : `${bundle.qty} bezels`);
      const popularLabel = data.bundlePopularLabel || "Popular";
      const saveLabel = data.bundleSaveLabel || "Save";
      const offLabel = data.bundleOffLabel || "";

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `bezel-bundle${selected ? " is-selected" : ""}${bundle.popular ? " is-popular" : ""}`;
      btn.setAttribute("role", "radio");
      btn.setAttribute("aria-checked", String(selected));

      btn.innerHTML = `
        ${bundle.popular ? `<span class="bezel-bundle__badge">${popularLabel}</span>` : ""}
        <span class="bezel-bundle__label">${label}</span>
        ${savings > 0 ? `<span class="bezel-bundle__compare">${formatMoney(compareAt)}</span>` : ""}
        <span class="bezel-bundle__price">${formatMoney(price)}</span>
        ${savings > 0 ? `<span class="bezel-bundle__save">${saveLabel} ${formatMoney(savings)} · ${percent}%${offLabel ? ` ${offLabel}` : ""}</span>` : ""}
      `;

      btn.addEventListener("click", () => {
        if (state.bundleQty === bundle.qty) {
          state.bundleQty = 0;
          clearExtras();
        } else {
          state.bundleQty = bundle.qty;
          preselectExtras(bundle.qty);
        }
        renderBundles();
        renderExtrasPicker();
        updateTotals();
      });

      container.appendChild(btn);
    });
  };

  const renderExtrasPicker = () => {
    const group = $("#extras-group", root);
    const grid = $("#extras-picker", root);
    const panel = $("#extras-panel", root) || group?.closest(".extras");
    if (!group || !grid) return;

    const slots = extraSlots();
    const open = state.bundleQty > 0 && slots > 0;
    group.hidden = !open;
    if (panel) panel.classList.toggle("is-open", open);
    if (!open) {
      updateExtrasStatus();
      return;
    }

    grid.innerHTML = "";
    const atCap = totalExtraQty() >= slots;

    data.bezels
      .filter((b) => b.handle !== state.currentHandle)
      .forEach((bezel) => {
        const isExtra = state.extraQty[bezel.handle] > 0;
        const cell = document.createElement("div");
        cell.className = "bezel-picker";

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "swatch swatch--bezel";
        btn.setAttribute("aria-hidden", "true");
        btn.tabIndex = -1;
        if (bezel.thumb) btn.style.setProperty("--swatch-image", `url("${bezel.thumb}")`);

        const checkLabel = document.createElement("label");
        checkLabel.className = "bezel-picker__check is-visible";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "bezel-picker__input";
        checkbox.checked = isExtra;
        checkbox.disabled = !isExtra && atCap;
        checkbox.setAttribute("aria-label", `Add ${bezel.label} to bundle`);

        checkbox.addEventListener("change", () => {
          if (checkbox.checked && totalExtraQty() >= slots) {
            checkbox.checked = false;
            return;
          }
          state.extraQty[bezel.handle] = checkbox.checked ? 1 : 0;
          renderExtrasPicker();
          updateTotals();
        });

        const checkText = document.createElement("span");
        checkText.textContent = bezel.label;

        checkLabel.append(checkbox, checkText);
        cell.append(btn, checkLabel);
        grid.appendChild(cell);
      });

    updateExtrasStatus();
  };

  const updateExtrasStatus = () => {
    const slots = extraSlots();
    const text = $("#extras-status-text", root);
    if (text) {
      text.textContent = `${totalExtraQty()} ${data.extrasStatusOf || "of"} ${slots} ${data.extrasStatusSuffix || "selected"}`;
    }
  };

  const orderTotal = () => {
    const product = currentBezel();
    const bundle = activeBundle();
    if (!bundle || state.bundleQty === 0 || totalExtraQty() === 0) {
      return product.price;
    }
    // Selected style + additive bundle (extras) price
    return product.price + bundlePricing(bundle).price;
  };

  const orderCompareAt = () => {
    const product = currentBezel();
    const bundle = activeBundle();
    const productCompare =
      product.compareAtPrice > product.price ? product.compareAtPrice : product.price;

    if (!bundle || state.bundleQty === 0 || totalExtraQty() === 0) {
      return product.compareAtPrice > product.price ? product.compareAtPrice : 0;
    }

    const { price, compareAt } = bundlePricing(bundle);
    const extrasCompare = Math.max(compareAt, price);
    const totalCompare = productCompare + extrasCompare;
    const totalPrice = product.price + price;
    return totalCompare > totalPrice ? totalCompare : 0;
  };

  const isPromoActive = () => {
    const pct = Number(data.promoDiscountPercent) || 0;
    if (!data.promoEnabled || pct <= 0) return false;
    const now = Date.now();
    if (data.promoEndMs && now >= data.promoEndMs) return false;
    if (data.promoStartMs && now < data.promoStartMs) return false;
    return true;
  };

  const applyPromoPricing = (total, compareAt) => {
    if (!isPromoActive()) return { price: total, compareAt: compareAt || 0 };
    const pct = Number(data.promoDiscountPercent) || 0;
    const crossed = total;
    const sale = Math.round((crossed * (100 - pct)) / 100);
    return { price: sale, compareAt: crossed };
  };

  const updatePriceDisplay = (priceEl, compareEl, price, compareAt) => {
    if (priceEl) priceEl.textContent = formatMoney(price);
    if (compareEl) {
      if (compareAt > price) {
        compareEl.textContent = formatMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    const savingsEl = $("#price-savings", root);
    const savingsAmount = $("#price-savings-amount", root);
    const savings = compareAt > price ? compareAt - price : 0;
    if (savingsEl && savingsAmount) {
      if (savings > 0) {
        savingsAmount.textContent = formatMoney(savings);
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }
  };

  const updateActionPrices = (price, compareAt) => {
    const pairs = [
      ["cta-total", "cta-compare"],
      ["sticky-total", "sticky-total-compare"],
      ["sticky-price", "sticky-compare"],
    ];

    pairs.forEach(([priceId, compareId]) => {
      const priceEl = document.getElementById(priceId);
      const compareEl = document.getElementById(compareId);
      if (priceEl) priceEl.textContent = formatMoney(price);
      if (!compareEl) return;
      if (compareAt > price) {
        compareEl.textContent = formatMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    });
  };

  const updateTotals = () => {
    const total = orderTotal();
    const compareAt = orderCompareAt();
    const priced = applyPromoPricing(total, compareAt);

    updatePriceDisplay($("#bezel-price", root), $("#bezel-compare-price", root), priced.price, priced.compareAt);
    updateActionPrices(priced.price, priced.compareAt);
  };

  const updateProductCopy = () => {
    const bezel = currentBezel();
    const title = `${bezel.label} Bezel`;

    document.title = `${title} — ${document.title.split("—").pop()?.trim() || "Zonate"}`;

    const titleEl = $("#product-title", root);
    if (titleEl) titleEl.textContent = title;

    const breadcrumb = $("#breadcrumb-current", root);
    if (breadcrumb) breadcrumb.textContent = bezel.label;

    const bezelLabel = $("#bezel-label", root);
    if (bezelLabel) bezelLabel.textContent = bezel.label;

    const detailsPanel = $$(".info-panel", root).find((panel) => {
      const summary = panel.querySelector("summary");
      return summary?.textContent?.trim() === "Bezel details";
    });
    const finishRow = detailsPanel?.querySelector("ul li:last-child");
    if (finishRow) finishRow.innerHTML = `<strong>Finish:</strong> ${bezel.label} aluminium`;

    const stickyName = document.getElementById("sticky-name");
    if (stickyName) stickyName.textContent = title;

    const stickyImage = document.getElementById("sticky-image");
    if (stickyImage && bezel.thumb) {
      stickyImage.src = bezel.thumb.replace("width=200", "width=120");
      stickyImage.alt = title;
    }

    const dial = activeDial();
    const upsellImg = $("#upsell-watch-img", root);
    if (upsellImg && dial?.previewImage) {
      upsellImg.src = dial.previewImage.replace("width=1200", "width=400");
      upsellImg.alt = `Pivot with ${bezel.label} bezel`;
    }

    const upsellLink = $("#upsell-watch-link", root);
    if (upsellLink && data.watchUpsell?.url) {
      upsellLink.href = data.watchUpsell.url;
    }
  };

  const getCartDrawerSectionIds = () => {
    const sections = [];
    document.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
    );

    if (!sections.length) {
      const drawer = document.getElementById("cart-drawer") || document.querySelector("cart-drawer");
      const sectionEl = drawer?.closest("[id^='shopify-section-']");
      if (sectionEl) sections.push(sectionEl.id.replace("shopify-section-", ""));
    }

    return sections;
  };

  const fetchCartDrawerSections = async (sectionIds) => {
    const shopRoot = window.Shopify?.routes?.root || "/";
    const sections = {};
    const bust = Date.now();

    await Promise.all(
      sectionIds.map(async (id) => {
        try {
          const jsonRes = await fetch(
            `${shopRoot}?sections=${encodeURIComponent(id)}&_=${bust}`,
            { credentials: "same-origin", headers: { Accept: "application/json" } }
          );
          if (jsonRes.ok) {
            const json = await jsonRes.json();
            if (json?.[id]) {
              sections[id] = json[id];
              return;
            }
          }
        } catch {
          /* fall through */
        }

        const response = await fetch(
          `${shopRoot}?section_id=${encodeURIComponent(id)}&_=${bust}`,
          { credentials: "same-origin" }
        );
        sections[id] = await response.text();
      })
    );

    return sections;
  };

  const buildCartItems = () => {
    const product = currentBezel();
    const items = [{ id: product.variantId, quantity: 1 }];

    data.bezels.forEach((bezel) => {
      if (bezel.handle !== state.currentHandle && state.extraQty[bezel.handle] > 0 && bezel.variantId) {
        items.push({ id: bezel.variantId, quantity: 1 });
      }
    });

    return items;
  };

  const addToCart = async () => {
    const product = currentBezel();
    const btn = $("#add-to-cart", root);
    if (!product.variantId || !product.available) return;

    const items = buildCartItems();
    const sectionIds = getCartDrawerSectionIds();
    const originalText = btn?.innerHTML;

    if (btn) {
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
    }

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const shopRoot = window.Shopify?.routes?.root || "/";
      const payload = { items };

      if (sectionIds.length) {
        payload.sections = sectionIds.join(",");
        payload.sections_url = `${window.location.pathname}${window.location.search}`;
      }

      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: "POST",
        body: JSON.stringify(payload),
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (!response.ok) {
        throw new Error(responseJson?.description || responseJson?.message || "Add to cart failed");
      }

      const cartContent = await (
        await fetch(`${shopRoot}cart.js`, { credentials: "same-origin" })
      ).json();
      cartContent.sections = sectionIds.length
        ? await fetchCartDrawerSections(sectionIds)
        : responseJson.sections || {};

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            onSuccessDo: "force_open_drawer",
            cart: cartContent,
          },
        })
      );

      if (btn) {
        btn.innerHTML = data.addedLabel || "Added!";
        setTimeout(() => {
          btn.innerHTML = originalText;
          updateTotals();
          btn.disabled = false;
          btn.removeAttribute("aria-busy");
        }, 1500);
      }
    } catch (err) {
      console.error("RS Bezel PDP: add to cart error", err);
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute("aria-busy");
      }
    }
  };

  const initStickyBar = () => {
    const bar = document.getElementById("sticky-bar");
    const buyBoxActions = $(".buy-box__actions", root);
    const stickyAdd = document.getElementById("sticky-add");
    const mainAdd = $("#add-to-cart", root);

    if (!bar || !buyBoxActions) return;

    const setVisible = (visible) => {
      bar.classList.toggle("is-visible", visible);
      bar.setAttribute("aria-hidden", String(!visible));
      document.body.classList.toggle("has-sticky-atc", visible);
    };

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" }
    );
    observer.observe(buyBoxActions);
    stickyAdd?.addEventListener("click", () => mainAdd?.click());
  };

  const init = () => {
    renderGallery();
    renderBezelSwatches();
    renderDialSwatches();
    renderBundles();
    renderExtrasPicker();
    updateProductCopy();
    updateTotals();

    root.addEventListener("rs:countdown-change", updateTotals);
    $("#add-to-cart", root)?.addEventListener("click", addToCart);
    if (data.showStickyAtc) initStickyBar();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
